import React, { useCallback, useEffect, useRef, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";

import api from "../../../../api";
import { UserFilterOptions } from "../UserFilters";
import { IVatDeclarationUser } from "../../../../types";
import VatTable from "./VatTableView";
import {
  destroyMessage,
  showSuccessMessage,
  showLoadingMessage,
} from "../../../../utils";
import { EMAIL_PARAM_NAME } from "../../../hooks/useEmailParam";
import { useIsWithinTabs } from "../../../hooks/useWithinTabs";
import {
  DEFAULT_VAT_FILTER_OPTIONS,
  seedVatListState,
  toVatListQuery,
  VAT_LIST_QUERY_CONFIG,
} from "./listQueryParams";

/*
 * SMT-1354 model: two independent things live in the URL.
 *
 * - Selection (`email`): which customer the sibling tabs (ELSTER, Submissions,
 *   DFV) work on. Set by expanding a row, a manually typed email filter, an
 *   external link (Alle Mandanten / the customer drawer), and cleared by
 *   Load all. Read by the list only on arrival from outside the VAT tabs.
 * - List state (`all`, `filterEmail`, `status`, `frequency`, `plans`,
 *   `assigneeId`, `page`): every list interaction — see `listQueryParams.ts`.
 *   Expanding a row never touches it, so returning from a sibling tab leaves
 *   the working list exactly as it was.
 *
 * `seedVatListState` resolves both into the container's initial state once,
 * on mount; `toVatListQuery` mirrors the list state back into the URL.
 */
const VatTableContainer = () => {
  const [query, setQuery] = useQueryParams({
    [EMAIL_PARAM_NAME]: StringParam,
    ...VAT_LIST_QUERY_CONFIG,
  });
  const withinTabs = useIsWithinTabs();

  const [users, setUsers] = useState<Array<IVatDeclarationUser> | null>(null);
  const [loading, setLoading] = useState(false);
  // Filter, page and Load all can overlap; the spinner stays on until the last one settles.
  const pendingFetches = useRef(0);

  // Seed once from the URL as it was on mount; later URL changes (all driven
  // by this component itself) must not re-seed the list.
  const [seed] = useState(() =>
    seedVatListState(query, { emailParam: query.email, withinTabs })
  );
  const [pageNumberState, setCurrentPage] = useState(seed.page);
  const [filterOptionsState, setFilterOptions] = useState<UserFilterOptions>(
    seed.filterOptions
  );

  const fetchUsers = useCallback(
    async (pageNumber?: number, filterOptions?: UserFilterOptions) => {
      const loadingKey = "user-declarations";
      try {
        pendingFetches.current += 1;
        setLoading(true);
        showLoadingMessage(loadingKey);
        const { declarations, page } =
          await api.kontax.getUsersWithDeclarations({
            page: pageNumber,
            status: filterOptions?.status,
            email: filterOptions?.email,
            plans: filterOptions?.plans,
            vatPaymentFrequency: filterOptions?.vatPaymentFrequency,
            assigneeId: filterOptions?.assigneeId,
          });

        setUsers(declarations);
        setCurrentPage(page);
        showSuccessMessage();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("Error fetching user", error);
        setUsers([]);
        setCurrentPage(1);
      } finally {
        pendingFetches.current -= 1;
        setLoading(pendingFetches.current > 0);
        destroyMessage(loadingKey);
      }
    },
    []
  );

  useEffect(() => {
    if (seed.shouldFetch) {
      fetchUsers(seed.page, seed.filterOptions);
    }

    // An arrival with a selected customer (`?email=`, no `filterEmail`) seeds
    // the list to them, same as today — but that seed only lives in local
    // state. Mirror it into `filterEmail` so a later return from a sibling
    // tab (which sets `withinTabs` but never touches `filterEmail`) still
    // finds the list filtered to this customer, instead of coming back
    // empty. `replaceIn` so this doesn't add its own history entry.
    if (seed.filterOptions.email && !query.filterEmail) {
      setQuery({ filterEmail: seed.filterOptions.email }, "replaceIn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expand only selects the customer for the sibling tabs; it must never
  // narrow or otherwise change the list (SMT-1354).
  const onSelectCustomer = useCallback(
    (email: string) => {
      setQuery({ email });
    },
    [setQuery]
  );

  return (
    <VatTable
      users={users}
      loading={loading}
      filterOptions={filterOptionsState}
      handleFilterChange={(newFilterOptions: UserFilterOptions) => {
        const merged = { ...DEFAULT_VAT_FILTER_OPTIONS, ...newFilterOptions };
        setFilterOptions(merged);
        fetchUsers(pageNumberState, newFilterOptions);

        // A typed email filter also selects the customer (as today), so it
        // is written to `email` too — but only when it actually changed:
        // changing status/plan/assignee must not wipe an existing selection.
        const emailChanged =
          newFilterOptions.email !== filterOptionsState.email;

        setQuery({
          ...toVatListQuery(merged, pageNumberState),
          all: newFilterOptions.email ? undefined : query.all,
          ...(emailChanged
            ? { email: newFilterOptions.email || undefined }
            : {}),
        });
      }}
      pageNumber={pageNumberState}
      handlePageChange={(newPageNumber: number) => {
        setCurrentPage(newPageNumber);
        fetchUsers(newPageNumber, filterOptionsState);
        setQuery({ page: newPageNumber > 1 ? newPageNumber : undefined });
      }}
      loadAll={() => {
        const cleared = { ...filterOptionsState, email: "" };
        setFilterOptions(cleared);
        fetchUsers(1, cleared);
        setCurrentPage(1);
        // SMT-1279: Load all is a fresh browse, not a narrowed selection, so
        // it clears `email` too — otherwise ELSTER/Submissions/DFV would keep
        // pointing at whoever was selected before "Load all" was clicked.
        setQuery({
          all: true,
          filterEmail: undefined,
          email: undefined,
          page: undefined,
        });
      }}
      onSelectCustomer={onSelectCustomer}
    />
  );
};

export default VatTableContainer;
